import { useEffect } from 'react';
import { NavLink, Outlet, useLocation, useNavigate, useParams, useOutletContext } from 'react-router-dom';
import { MOBIL_TABS, TABS } from '../App.jsx';
import { useAuftrag } from '../hooks/data.js';
import { useTimer } from '../state/timer.jsx';
import { rememberAuftrag } from '../lib/storage.js';
import { clock } from '../lib/format.js';
import { Icon, StopIcon } from '../components/Icon.jsx';
import { OutboxBanner } from '../state/outbox.jsx';
import { useFunktionen } from '../hooks/useFunktionen.js';
import { useBegriffe } from '../hooks/useBegriffe.js';
import { useFormularVorlagen } from '../hooks/data.js';

/** Rahmen für alles innerhalb eines Auftrags: Sidebar (Desktop), Kopf + Bottom-Nav (Mobile). */
export function AuftragLayout() {
  const { nr } = useParams();
  const auftrag = useAuftrag(nr);
  const timer = useTimer();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const base = `/auftrag/${encodeURIComponent(nr)}`;
  const hat = useFunktionen();
  const b = useBegriffe();
  const tabs = TABS.filter((t) => !t.funktion || t.funktion.some(hat)); // nur Bereiche, die die Firma hat
  const mobilTabs = MOBIL_TABS.filter((t) => !t.funktion || t.funktion.some(hat));
  const tabPath = pathname.slice(base.length).replace(/^\//, '').split('/')[0];
  const tab = TABS.find((t) => t.path === tabPath);
  const vorlagen = useFormularVorlagen({ laden: tabPath === 'formulare' }).data;
  const imFormular = tabPath === 'formulare' && pathname.slice(base.length).split('/').filter(Boolean).length >= 3;
  const formular = tabPath === 'formulare' ? vorlagen?.find((x) => x.id === pathname.slice(base.length).split('/')[2]) : null;
  const current = tab ? { ...tab, long: b(tab.long) } : ({ mehr: { path: 'mehr', long: `Mehr zum ${b('auftrag')}` }, checkliste: { path: 'checkliste', long: 'Übersichtslisten Baustelle' }, plaene: { path: 'plaene', long: 'Bewässerungspläne' }, formulare: { path: 'formulare', long: formular?.mehrzahl ?? 'Formulare' } }[tabPath] ?? { ...TABS[0], long: b(TABS[0].long) });

  useEffect(() => { if (auftrag.data) rememberAuftrag(auftrag.data); }, [auftrag.data]);
  useEffect(() => { document.querySelector('.scroll')?.scrollTo({ top: 0 }); }, [tabPath]);

  const a = auftrag.data;
  const elsewhere = timer.running && timer.nummer !== nr;
  const showMini = timer.running && (current.path !== 'zeit-material' || elsewhere);
  const goSearch = () => navigate('/');

  if (auftrag.isError) {
    return (
      <div className="app"><div className="scroll"><div className="swrap">
        <div className="empty" role="alert">
          {auftrag.error.status === 404 ? `Auftrag ${nr} wurde nicht gefunden.` : auftrag.error.message}
          <button className="btn" onClick={goSearch}>Zur Auftragssuche</button>
        </div>
      </div></div></div>
    );
  }

  return (
    <div className="app">
      <aside className="side">
        <div className="row" style={{ padding: '0 6px' }}>
          <span style={{ width: 34, height: 34, borderRadius: 10, background: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon name="marke" size={22} stroke={2.6} /></span>
          <span style={{ fontSize: 16, fontWeight: 800 }}>{b('produkt')}</span>
        </div>
        <button className="btn ghost" style={{ justifyContent: 'flex-start', borderRadius: 14 }} onClick={goSearch}>
          <Icon name="search" size={18} stroke={2.2} />{b('auftrag')} wechseln
        </button>
        <div style={{ padding: 14, borderRadius: 16, background: 'rgba(255,255,255,0.06)' }} className="stack">
          <span className="on-dark-muted" style={{ fontSize: 12, fontWeight: 700 }}>{nr}</span>
          <span style={{ fontSize: 16, fontWeight: 800, lineHeight: 1.25 }}>{a?.titel ?? '…'}</span>
          <span className="on-dark-muted" style={{ fontSize: 13 }}>{a?.kunde?.ort}</span>
        </div>
        <nav aria-label="Bereiche" className="stack">
          {tabs.map((t) => (
            <NavLink key={t.path} to={t.path ? `${base}/${t.path}` : base} end={!t.path} className={({ isActive }) => `sidenav${isActive ? ' on' : ''}`}>
              <Icon name={t.icon} />
              <span style={{ flex: 1 }}>{b(t.long)}</span>
            </NavLink>
          ))}
        </nav>
        <SideTimer nr={nr} titel={a?.titel} />
      </aside>

      <div className="main">
        <header className="mtop">
          <button className="iconbtn" aria-label="Zurück zur Auftragssuche" onClick={goSearch}><Icon name="back" stroke={2.4} /></button>
          <button onClick={goSearch} className="plainbtn" style={{ gap: 0 }}>
            <span className="ellipsis" style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)' }}>{nr}{a?.kunde?.ort ? ` · ${a.kunde.ort}` : ''}</span>
            <span className="ellipsis" style={{ fontSize: 16, fontWeight: 800 }}>{a?.titel ?? 'Lädt …'}</span>
          </button>
          <button className="iconbtn" aria-label="Anderen Auftrag suchen" onClick={goSearch}><Icon name="search" stroke={2.2} /></button>
        </header>

        <div className="scroll">
          <div className="wrap">
            {/* im Formular-Editor zeigt der Editor selbst, wo man ist – mehr Platz für die Eingaben */}
            <div className={imFormular ? 'sr-only' : 'stack'} style={{ gap: 2 }}>
              <span className="sub" style={{ fontWeight: 700 }}>{nr}{a?.kunde?.firma ? ` · ${a.kunde.firma}` : ''}</span>
              <h1 className="h1">{current.long}</h1>
            </div>
            <OutboxBanner />
            {a ? <Outlet context={{ nr, auftrag: a, base }} /> : <div className="skeleton" style={{ height: 240 }} />}
          </div>
        </div>

        {showMini && (
          <div className="minibar">
            <span className="dot live" />
            <button onClick={() => navigate(`/auftrag/${encodeURIComponent(timer.nummer)}/zeit-material`)} style={{ all: 'unset', cursor: 'pointer', flex: 1, minWidth: 0, display: 'flex', alignItems: 'baseline', gap: 10 }}>
              <span className="tn" style={{ fontSize: 18, fontWeight: 800 }}>{clock(timer.elapsedSec)}</span>
              <span className="ellipsis on-dark-muted" style={{ fontSize: 12, fontWeight: 600 }}>{elsewhere ? `auf ${timer.nummer} · ` : ''}{timer.beschreibung || 'Arbeitszeit'}</span>
            </button>
            {elsewhere && <button className="btn ghost" style={{ minHeight: 40, padding: '0 12px', fontSize: 13 }} onClick={() => timer.switchTo({ nummer: nr, titel: a?.titel })} disabled={timer.busy}>Hierher</button>}
            <button className="btn stop" style={{ minHeight: 40, width: 44, padding: 0, borderRadius: 12 }} aria-label="Zeit stoppen und buchen" onClick={timer.stop} disabled={timer.busy}><StopIcon size={14} /></button>
          </div>
        )}

        <nav className="bnav" aria-label="Bereiche" style={{ '--nav-n': mobilTabs.length }}>
          {mobilTabs.map((t) => (
            <NavLink key={t.path} to={t.path ? `${base}/${t.path}` : base} end={!t.path}
              className={({ isActive }) => `navbtn${isActive || t.unter?.includes(tabPath) ? ' on' : ''}`}
              aria-current={t.unter?.includes(tabPath) ? 'page' : undefined}>
              <span className="ico">
                <Icon name={t.icon} size={22} />
              </span>
              <span className="lbl">{b(t.label)}</span>
            </NavLink>
          ))}
        </nav>
      </div>
    </div>
  );
}

function SideTimer({ nr, titel }) {
  const timer = useTimer();
  const navigate = useNavigate();
  const elsewhere = timer.running && timer.nummer !== nr;
  return (
    <div style={{ marginTop: 'auto', padding: 16, borderRadius: 18, background: 'rgba(255,255,255,0.06)' }} className="stack">
      <span className="row on-dark-muted" style={{ gap: 8, fontSize: 12, fontWeight: 700 }}>
        <span className={`dot${timer.running ? ' live' : ''}`} />
        {timer.running ? `Läuft auf ${timer.nummer}` : 'Keine Zeit aktiv'}
      </span>
      <span className="tn" style={{ fontSize: 30, fontWeight: 800 }}>{clock(timer.elapsedSec)}</span>
      {elsewhere && <button className="btn ghost" style={{ minHeight: 40, fontSize: 13 }} onClick={() => timer.switchTo({ nummer: nr, titel })} disabled={timer.busy}>Auf {nr} wechseln</button>}
      {timer.running
        ? <button className="btn stop" onClick={timer.stop} disabled={timer.busy}>Stoppen &amp; buchen</button>
        : <button className="btn" onClick={() => navigate(`/auftrag/${encodeURIComponent(nr)}/zeit-material`)}>Zeit erfassen</button>}
    </div>
  );
}

/** Zugriff auf { nr, auftrag, base } in den Unterseiten. */
export const useAuftragCtx = () => useOutletContext();
